/**
 * Keeps a page's dated reads fresh (1.Y deliverable 2): a `chrono.changed` touching the page
 * refetches its states and placements, a `links.changed` naming it its links, and
 * `clock.changed` NOW (1.O-1's `whenKeys.clock`).
 */
import { chronoChangeTouches } from '@codex/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { whenKeys } from '../when/calendar-context.js';
import { temporalKeys, useTemporalApi } from './api.js';

export function useTemporalEvents(docId: string, enabled = true): void {
  const services = useTemporalApi();
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const offChrono = services.subscribe('chrono.changed', (event) => {
      if (!chronoChangeTouches(event, docId)) return;
      void client.invalidateQueries({ queryKey: temporalKeys.states(docId) });
      void client.invalidateQueries({ queryKey: temporalKeys.placements(docId) });
    });
    const offLinks = services.subscribe('links.changed', (event) => {
      if (!event.data.docIds.includes(docId)) return;
      void client.invalidateQueries({ queryKey: temporalKeys.links(docId) });
    });
    const offClock = services.subscribe('clock.changed', () => {
      void client.invalidateQueries({ queryKey: whenKeys.clock });
    });
    return () => {
      offChrono();
      offLinks();
      offClock();
    };
  }, [services, client, docId, enabled]);
}
